import { HwTypeKey } from "@const/hwTypeKey.constants";
import { HostCfgDTO } from "@dto/hostCfgResponse.dto";
import { ITestSection } from "@dto/microserviceResponse.dto";
import { VendorWithModelsDTO } from "@dto/vendor.dto";
import { FC, useEffect, useRef, useState } from "react";
import { FaCheck, FaExclamationTriangle, FaTimes } from "react-icons/fa";
import { XmModal } from "src/component/modal/XmModal";
import { Log } from "src/service/log.service";
import { GLOB } from "src/util/Glob";
import "../import.less";
import { postDisableHostCfgsAPI, postUpdatedSwithcesAPI } from "./api/ImportAPI";
import { ImportDeviceTableRow } from "./importDeviceTableRow";

interface Props {
    devices: HostCfgDTO[];
    disableOnFail?: boolean;
    vendors: VendorWithModelsDTO[];
}

export const hwTypeMap = {
    "power": "IBM Power Systems",
    "vmware": "VMware",
    "nutynix": "Nutanix",
    "proxmox": "Proxmox",
    "oraclevm": "Oracle VM",
    'aws': "Amazon Web Services",
    "azure": "Microsoft Azure",
    "docker": "Docker",
    "kubernetes": "Kubernetes",
    "postgres": "PostgreSQL",
    "sqlserver": "Microsoft SQL Server",
    "oracledb": "Oracle",
    "db2": "IBM Db2",
    "gcloud": "Google Cloud",
    "ovirt": "oVirt",
    "idrac": "Dell iDRAC",
};

export class HostCfgWithStatus extends HostCfgDTO {
    status: "success" | "failure" | "unknown";
    sections: ITestSection[] = [];
}

export const ImportConnectionTest: FC<Props> = (props) => {

    const { devices, disableOnFail = true, vendors } = props;
    const [devicesWithStatus, setDevicesWithStatus] = useState<HostCfgWithStatus[]>(devices.map(device => { return { ...device, status: "unknown", sections: [] }; }));
    const [isStatusShown, setIsStatusShown] = useState<boolean>(false);
    const [shownStatusId, setShownStatusId] = useState<string>(null); const [devicesToDisable, setDevicesToDisable] = useState([]);
    const [isFinished, setIsFinished] = useState(false);
    const [updatedSwitches, setUpdatedSwitches] = useState([]);
    const webSocket = useRef<WebSocket>();

    const deviceConnectionTest = async (device: HostCfgDTO) => {

        await new Promise(resolve => setTimeout(resolve, 1000));    //sleep 1000ms cos some service didnt have time to start listening but you were already able to connect to it, quite retarded right?

        return new Promise<void>((resolve) => {
            const deviceWithStatus: HostCfgWithStatus = { ...device, status: "unknown", sections: [] };

            webSocket.current?.close();
            const ws = webSocket.current = new WebSocket(GLOB.SERVER_WS_URL + "/websocket/mstest?hostcfg_id=" + device.hostcfg_id);

            ws.onerror = ev => {
                deviceWithStatus.status = "failure";
                setDevicesToDisable((prevState) => [...prevState, device]);

                setDevicesWithStatus((devicesWithStatus) => {
                    const index = devicesWithStatus.findIndex(dev => dev.hostcfg_id === deviceWithStatus.hostcfg_id);
                    const tmpDevices = [...devicesWithStatus];
                    tmpDevices[index] = deviceWithStatus;
                    return tmpDevices;
                });
                webSocket.current?.close();
                resolve();
            };
            ws.onclose = ev => {
                deviceWithStatus.status = "success";

                deviceWithStatus.sections.forEach(section => {
                    if (!section.success) {
                        deviceWithStatus.status = "failure";
                        setDevicesToDisable((prevState) => [...prevState, device]);
                    }
                });
                if ([HwTypeKey.LANCISCO, HwTypeKey.LANOTHERS, HwTypeKey.FIREWALLCISCO, HwTypeKey.SANBRCD, HwTypeKey.SANCISCO].includes(deviceWithStatus.hw_type as HwTypeKey)) {
                    const labelSection = deviceWithStatus.sections.find(sec => sec.label === "SNMP connection" && sec.success);
                    if (labelSection) {
                        if (labelSection.text?.length) {
                            const label = labelSection.text?.at(-1).trimEnd();
                            deviceWithStatus.label = label;
                            setUpdatedSwitches((prevState) => [...prevState, { ...device, label: labelSection.text?.at(-1).trimEnd() }]);
                        }
                    }
                }
                setDevicesWithStatus((devicesWithStatus) => {
                    const index = devicesWithStatus.findIndex(dev => dev.hostcfg_id === deviceWithStatus.hostcfg_id);
                    const tmpDevices = [...devicesWithStatus];
                    tmpDevices[index] = deviceWithStatus;
                    return tmpDevices;
                });


                webSocket.current?.close();
                resolve();
            };
            ws.onmessage = ev => {
                const msg: ITestSection = JSON.parse(ev.data);
                deviceWithStatus.sections = [...deviceWithStatus.sections, msg];
            };
        });


    };

    const handleDeviceUpdates = async () => {
        try {
            if (disableOnFail && devicesToDisable?.length) {
                await postDisableHostCfgsAPI(devicesToDisable);
                setDevicesToDisable([]);
            }
            if (updatedSwitches?.length) {
                await postUpdatedSwithcesAPI(updatedSwitches);
                setUpdatedSwitches([]);
            }
        } catch (error) {
            Log.error(error);
        }
    };

    const awaitDeviceConnectionTest = async () => {
        for (const device of devicesWithStatus) {
            await deviceConnectionTest(device);
        }
        setIsFinished(true);
    };

    const showSections = (device: HostCfgWithStatus) => {
        setShownStatusId(device.hostcfg_id);
        setIsStatusShown(true);
    };

    const closeSections = async () => {
        await handleDeviceUpdates();
        setShownStatusId(null);
        setIsStatusShown(false);
    };

    useEffect(() => {
        if (isFinished) {
            void handleDeviceUpdates();
            setIsFinished(false);
        }
    }, [isFinished]);

    useEffect(() => {
        void awaitDeviceConnectionTest();

        return () => {
            webSocket.current?.close();
        };
    }, []);

    return (
        <>
            <table className="xm-connection-table">
                <thead>
                    <tr>
                        <th>Label</th>
                        <th>Device</th>
                        <th>Connection Test</th>
                        <th>Connection Info</th>
                    </tr>
                </thead>
                <tbody>
                    {devicesWithStatus.length > 0 && devicesWithStatus.map((device) => {
                        const vendor = vendors.find(v => v.vendor_key === device.vendor_key);
                        const vendorLabel = vendor?.vendor_label ?? "";
                        const deviceLabel = vendor?.models?.find(m => m.model_key === device.model_key)?.label ?? "";
                        if (vendorLabel === "") {
                            if (hwTypeMap[device.hw_type] && device.data.type !== "CMC") {
                                return (
                                    <ImportDeviceTableRow
                                        key={device.hostcfg_id}
                                        device={device}
                                        deviceType={hwTypeMap[device.hw_type]}
                                        showSections={showSections}
                                    />
                                );
                            }
                            return (
                                <ImportDeviceTableRow
                                    key={device.hostcfg_id}
                                    device={device}
                                    deviceType={(device.data.device ? device.data.device : "")}
                                    showSections={showSections}
                                />
                            );
                        }
                        return (
                            <ImportDeviceTableRow
                                key={device.hostcfg_id}
                                device={device}
                                deviceType={`${vendorLabel} ${deviceLabel}`}
                                showSections={showSections}
                            />
                        );
                    })}
                </tbody>
            </table>
            <XmModal
                open={isStatusShown}
                destroyOnHidden={true}
                closable={false} mask={XmModal.MASK_NOT_CLOSABLE}
                cancelButtonProps={{ style: { display: 'none' } }}
                okText={'Close'}
                onCancel={closeSections}
                onOk={closeSections}
            >
                {devicesWithStatus.find(d => d.hostcfg_id === shownStatusId) &&
                    <>
                        <span>Connection test results:</span>
                        <table className="xm-connection-table">
                            <tbody>
                                {devicesWithStatus.find(d => d.hostcfg_id === shownStatusId).sections.map(ts => <tr key={ts.label}>
                                    <td>
                                        <strong>{ts.label}</strong>
                                        <div style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: ts.text }}></div>
                                    </td>
                                    <td>{ts.success ? <FaCheck className="xm-connection-status" color="darkgreen" />
                                        : ts.acceptable ? <FaExclamationTriangle className="xm-connection-status" color="darkorange" />
                                            : <FaTimes className="xm-connection-status" color="darkred" />}</td>
                                </tr>)}
                            </tbody>
                        </table>
                    </>
                }
            </XmModal>
        </>
    );

};
